// Nexora — abonnement Web Push réel (notification système, même écran verrouillé/app
// fermée), porté depuis StoreFlowProV2/public/sw-register.js. Consomme les vraies routes
// déjà réelles et déployées côté serveur (GET /api/push/public-key, POST /api/push/
// subscribe|unsubscribe-all, GET /api/push/status, POST /api/push/test) via le proxy /api/*
// déjà en place — aucun changement serveur nécessaire.
//
// iOS/Safari refuse silencieusement Notification.requestPermission() si l'appel n'est pas
// déclenché DIRECTEMENT par un geste utilisateur (tap), sans await avant dans la même pile
// d'appels — sinon il renvoie "denied" sans même afficher la popup, et ce refus reste
// ensuite enregistré côté iOS. `enable()` doit donc rester le tout premier appel async
// après un onClick, jamais précédé d'un await.

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";
}

export function pushPermission() {
  return typeof Notification !== "undefined" ? Notification.permission : "unsupported";
}

let swRegistrationPromise = null;
export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return Promise.resolve(null);
  if (!swRegistrationPromise) {
    swRegistrationPromise = navigator.serviceWorker.register("/service-worker.js", { scope: "/" }).catch((err) => {
      console.warn("[push] enregistrement du service worker impossible :", err.message);
      return null;
    });
  }
  return swRegistrationPromise;
}

async function doSubscribe() {
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();

  const subscription = existing || await (async () => {
    const { publicKey } = await fetch("/api/push/public-key").then((r) => r.json());
    if (!publicKey) return null;
    return registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  })();

  if (!subscription) return false;

  await fetch("/api/push/subscribe", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscription }),
  });
  return true;
}

// À appeler au chargement, une fois connecté — réabonne silencieusement si la permission
// est déjà accordée (réinstallation/nouvel appareil déjà autorisé), ne demande jamais la
// permission toute seule (voir contrainte de geste ci-dessus).
export async function initPushIfAlreadyGranted() {
  if (!pushSupported()) return;
  await registerServiceWorker();
  if (Notification.permission === "granted") {
    try { await doSubscribe(); } catch (err) { console.warn("[push] abonnement impossible :", err.message); }
  }
}

// Premier appel async après un vrai tap (bouton "Activer" des Réglages) — ne rejette
// jamais, renvoie un statut lisible plutôt qu'une exception non rattrapée.
export async function enablePush() {
  if (!pushSupported()) return "unsupported";
  let permission;
  try {
    permission = await Notification.requestPermission();
  } catch (err) {
    console.error("[push] Notification.requestPermission() a échoué :", err);
    return "error";
  }
  if (permission !== "granted") return permission;
  await registerServiceWorker();
  try { await doSubscribe(); } catch (err) { console.warn("[push] abonnement impossible :", err.message); }
  return permission;
}

// La permission navigateur "granted" ne peut jamais être révoquée par du JS — seul
// l'ABONNEMENT (où les notifications sont réellement envoyées) est sous contrôle de
// l'app. Le nettoyage serveur (tous les appareils du compte) prime toujours sur le
// nettoyage local (un seul appareil, peut être lent/bloqué) — voir /api/push/status.
export async function disablePush() {
  let serverOk = true;
  try {
    await fetch("/api/push/unsubscribe-all", { method: "POST", credentials: "include" });
  } catch (err) {
    console.error("[push] désabonnement compte (serveur) impossible :", err);
    serverOk = false;
  }
  try {
    await Promise.race([
      (async () => {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
      })(),
      new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 4000)),
    ]);
  } catch (err) {
    console.warn("[push] désabonnement local ignoré (sans conséquence) :", err.message);
  }
  return serverOk;
}

export async function fetchPushStatus() {
  try {
    const res = await fetch("/api/push/status", { credentials: "include" });
    const data = await res.json();
    return Boolean(data.subscribed);
  } catch {
    return false;
  }
}

export async function sendTestPush() {
  const res = await fetch("/api/push/test", { method: "POST", credentials: "include" });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Envoi du test impossible.");
  return data;
}
